// libs
import { useCallback } from 'react';
import omit from 'lodash/omit';

import { useTranslation } from '@lyearn/i18n';
// components
import { useSnackbar } from '@lyearn/ui';
// types
import { CreateScorecardInput, UpdateScorecardInput } from '@/types/schema';

import { ScorecardCreatorFormValues } from '../../views/ScorecardCreator/config/form';
// hooks
import { useCreateScorecardMutation } from '../../views/ScorecardCreator/graphql/mutations/generated/createScorecard';
import { useUpdateScorecardMutation } from '../../views/ScorecardCreator/graphql/mutations/generated/updateScorecard';

function useScorecardActions(scorecardId?: string, onSuccessCb?: () => void) {
  const { t } = useTranslation(['Scorecard', 'Exercise']);
  const { showSnackbar, showErrorSnackbar } = useSnackbar();
  const [createdScorecardResult, executeCreateScorecard] = useCreateScorecardMutation();
  const [updatedScorecardResult, executeUpdateScorecard] = useUpdateScorecardMutation();

  const onSuccess = useCallback(() => {
    showSnackbar({
      message: scorecardId
        ? t('Scorecard:success.UpdateScorecard')
        : t('Scorecard:success.CreateScorecard'),
    });
    onSuccessCb && onSuccessCb();
  }, [onSuccessCb, scorecardId, showSnackbar, t]);

  const onError = useCallback(() => {
    showErrorSnackbar({
      message: scorecardId
        ? t('Scorecard:error.UpdateScorecard')
        : t('Scorecard:error.CreateScorecard'),
    });
  }, [scorecardId, showErrorSnackbar, t]);

  const onSubmit = useCallback(
    async (values: ScorecardCreatorFormValues) => {
      const { title, skillId, questions } = values;
      const input = {
        title,
        skillId,
        questions: questions?.map((question) => omit(question, 'tempId', 'id')),
      };

      const onCreate = async () => {
        const result = await executeCreateScorecard({
          input: input as CreateScorecardInput,
        });

        result.data?.createScorecard.success ? onSuccess() : onError();
      };

      const onUpdate = async () => {
        const result = await executeUpdateScorecard({
          input: { scorecardId, ...input } as UpdateScorecardInput,
        });

        result.data?.updateScorecard.success ? onSuccess() : onError();
      };

      scorecardId ? await onUpdate() : await onCreate();
    },
    [executeCreateScorecard, executeUpdateScorecard, onError, onSuccess, scorecardId],
  );

  return {
    disabled: createdScorecardResult.fetching || updatedScorecardResult.fetching,
    onSubmit,
  };
}

export default useScorecardActions;
